import { useState } from 'react';

import classNames from 'classnames';
import type { ControllerStateAndHelpers } from 'downshift';
import Downshift from 'downshift';
import { connect as formikConnect } from 'formik';
import debounce from 'lodash/debounce';
import get from 'lodash/get'; // eslint-disable-line no-restricted-imports
import isFunction from 'lodash/isFunction';
import map from 'lodash/map';
import { compose, mapProps } from 'recompact';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { TextInputUnwired } from 'src/app/components/Form/TextInput';
import { TextInputDecoratedUnwired } from 'src/app/components/Form/TextInputDecorated';
import { useLazySegmentTrack } from 'src/app/hooks/useLazySegmentTrack';
import { extractValidationErrors } from 'src/app/popsicle/utils';

import style from './styles.module.scss';

const QUERY_MINIMUM_CHARS = 3;

const mapTextInputProps = mapProps(parentProps => {
  const { name, formik } = parentProps;
  const { touched, errors, values } = formik;

  /**
   * `get` being used here is NOT a simple case that optional chaining can replace.
   * The field names are in the same of `${string}.${index}` which is a syntax that `lodash.get`
   * implicitly understands how to dive into.
   *
   * DO NOT REPLACE THESE `get()` calls with optional chaining unless you've tested it.
   * touched?.[name] won't work. It would have to be something like:
   * touched?.[name.split('.')[0]]?.[name.split('.')[1]]
   */
  const error = get(touched, name) && get(errors, name);
  const value = get(values, name);

  return {
    ...parentProps,
    error,
    value,
  };
});

const FormikDownshiftTextInput = compose(formikConnect, mapTextInputProps)(TextInputUnwired);

const FormikDownshiftTextInputDecorated = compose(formikConnect, mapTextInputProps)(TextInputDecoratedUnwired);

const Renderer = ({
  dispatchFieldFocused, // mapDispatchToProps
  dispatchGetMedicationFormByIndex, // mapDispatchToProps
  dispatchUpdateMedicationForm, // mapDispatchToProps
  apiCall, // passed
  textInputProps, // passed
  selectedItemToString, // passed
  optionItemToString, // passed
  formik, // context
  setRelatedFields,
  noMatchingItemsText,
  compactStyle, // TextInput.propType
  dataCyText, //passed
  callbackToMedicationFields,
  doctorFormIndex,
  callbackToDrugSearch,
  icon,
}) => {
  const [items, setItems] = useState([]);
  const [trackAddOnboardingFormOtherMedication] = useLazySegmentTrack(UNIFIED.addOnboardingFormOtherMedication);

  const { name, extrasFieldName, onBlur, onFocus } = textInputProps;
  const { setFieldValue, handleBlur: formikHandleBlur, handleChange, setErrors } = formik;

  /**
   * debouncedApiCall
   * @param {*} searchQuery
   * @param {function} openMenu
   *
   * memoized apiCall that gets called on text input change
   */
  const debouncedApiCall = debounce(async (searchQuery, openMenu) => {
    try {
      const response = await apiCall(searchQuery);
      if (response?.length > 0) {
        setItems(response);
        openMenu();
      } else if (noMatchingItemsText) {
        setItems([{ noMatchingItemsText }]);
      }
    } catch (error) {
      setErrors(extractValidationErrors(error));
    }
  }, 200);

  const handleSelect = async (changes, stateAndHelpers: ControllerStateAndHelpers<any>) => {
    const { closeMenu, selectedItem, inputValue } = stateAndHelpers;

    if (callbackToMedicationFields) {
      callbackToMedicationFields(doctorFormIndex, selectedItem, inputValue);
    }

    const payload = {
      medication_id: selectedItem.id,
    };

    trackAddOnboardingFormOtherMedication(payload);

    setFieldValue(name, inputValue);
    if (extrasFieldName) setFieldValue(extrasFieldName, selectedItem);

    closeMenu();

    if (isFunction(setRelatedFields)) {
      setRelatedFields(selectedItem);
    }
  };

  const handleInputValueChange = async (inputValue, stateAndHelpers: ControllerStateAndHelpers<any>) => {
    const { openMenu, closeMenu } = stateAndHelpers;

    if (stateAndHelpers.type !== Downshift.stateChangeTypes.changeInput) {
      return;
    }

    if (callbackToDrugSearch) {
      const medicationFormAction = await dispatchGetMedicationFormByIndex(doctorFormIndex);
      if (medicationFormAction) {
        const { medicationForm } = medicationFormAction;
        if (medicationForm.showControlledDrugError) {
          const newMedicationForm = {
            ...medicationForm,
            showControlledDrugError: false,
          };
          dispatchUpdateMedicationForm(doctorFormIndex, newMedicationForm);
        }
      }

      callbackToDrugSearch(inputValue);
    }
    setFieldValue(name, inputValue);
    if (extrasFieldName) setFieldValue(extrasFieldName, null);

    const isValidSearchInput = inputValue && inputValue.length >= QUERY_MINIMUM_CHARS;

    if (isValidSearchInput) {
      debouncedApiCall(inputValue, openMenu);
    } else {
      if (callbackToMedicationFields) {
        callbackToMedicationFields(doctorFormIndex, false, inputValue);
      }
      setItems([]);
      closeMenu();
    }
  };

  const handleBlur = e => {
    formikHandleBlur(e);
    onBlur?.();
  };

  const TextInput = icon ? FormikDownshiftTextInputDecorated : FormikDownshiftTextInput;

  return (
    <Downshift onSelect={handleSelect} onInputValueChange={handleInputValueChange} itemToString={selectedItemToString}>
      {({ isOpen, openMenu, getLabelProps, getInputProps, getMenuProps, getItemProps, highlightedIndex }) => {
        const handleFocus = e => {
          openMenu();
          onFocus?.();
          dispatchFieldFocused(e);
        };

        return (
          <div className={style.component} role="combobox" aria-expanded={isOpen}>
            <TextInput
              {...getInputProps({
                ...textInputProps,
                onFocus: handleFocus,
                onBlur: handleBlur,
                onChange: handleChange,
              })}
              compactStyle={compactStyle}
              labelProps={getLabelProps({})}
            />
            <ul {...getMenuProps()}>
              {isOpen
                ? map(items, (item, index) => {
                    return item.noMatchingItemsText ? (
                      <li onMouseDown={e => e.preventDefault()} className={style.defaultText}>
                        {item.noMatchingItemsText}
                      </li>
                    ) : (
                      <li
                        {...getItemProps({ item })}
                        key={index}
                        className={classNames(style.suggestion, { [style.highlighted]: index === highlightedIndex })}
                        data-cy={dataCyText ? `${dataCyText}${index}` : undefined}
                      >
                        {optionItemToString(item)}
                      </li>
                    );
                  })
                : null}
            </ul>
          </div>
        );
      }}
    </Downshift>
  );
};

export { Renderer };
